import type { ExecutionProgressHydration } from '../../../executionOperations/types';
import {
  deriveRouteOperationalStatus,
  getRouteOperationalStatusColor,
  getRouteOperationalStatusLabel,
  type RouteOperationalStatusKey
} from '../routeOperationalStatus';
import type { ExecutionIncident } from '../incidentFacade';
import type { RouteStatusProjection, RouteTransportStatusKey } from './types';

function toTransportKey(
  key: RouteOperationalStatusKey,
  hydration: ExecutionProgressHydration
): RouteTransportStatusKey {
  if (key === 'ready_for_completion') return 'ready_for_completion';
  if (key !== 'problem') return key;

  const p = hydration.progress;
  if (p.executionComplete) return 'ready_for_completion';
  if (p.allUnloadingComplete) return 'ready_for_completion';
  if (p.allLoadingComplete) return 'in_transit';
  const anyLoading = hydration.stops.some(
    (s) => s.type === 'loading' && (s.loadingStartedAt || s.loadingCompletedAt)
  );
  if (anyLoading) return 'loading';
  return 'created';
}

/** Transport route status — incidents do not override transport phase. */
export function buildRouteStatusProjection(
  hydration: ExecutionProgressHydration | null | undefined,
  incidents: ExecutionIncident[] = []
): RouteStatusProjection {
  const withIncidents = deriveRouteOperationalStatus(hydration, incidents);

  if (!hydration) {
    return {
      transportKey: 'created',
      label: getRouteOperationalStatusLabel('created'),
      color: getRouteOperationalStatusColor('created'),
      hasExecutionFailure: false,
      failureReason: null
    };
  }

  const transportKey = toTransportKey(withIncidents.key, hydration);
  const hasExecutionFailure =
    withIncidents.hasFailure && withIncidents.key === 'problem' && !withIncidents.hasBlockingIncident;

  return {
    transportKey,
    label: getRouteOperationalStatusLabel(transportKey),
    color: getRouteOperationalStatusColor(transportKey),
    hasExecutionFailure,
    failureReason: hasExecutionFailure ? withIncidents.problemReason : null
  };
}
